import React, { useCallback, useMemo } from 'react';
import { HiMessage, HiSession } from '../../types';
import {
  convertHiMessageToLocal,
  isMessageByMe,
  isSystemMessage,
  isUnchargedOfferMessage,
  isUnreadMessageByOtherSide,
} from '../../utils';
import 'intersection-observer'; // optional polyfill
import Observer from '@researchgate/react-intersection-observer';
import UnchargedMessageItem from './UnchargedMessageItem';
import SystemMessageItem from './SystemMessageItem';
import NormalMessageItem from './NormalMessageItem';

const HiMessageItem = ({
  item: hiMessage,
  currentUserId,
  otherSideAccountName,
  otherSideId,
  otherSideUser,
  sessionId,
  chargeOffer,
  readMessage,
}: {
  item: HiMessage;
  currentUserId?: string;
  otherSideId: string;
  otherSideAccountName: string;
  sessionId: string | number;
  otherSideUser?: UserInfo;
  chargeOffer: (offerId: string, sessionId: HiSession['id'], messageId: HiMessage['id']) => void;
  readMessage: (id: HiMessage['id']) => void;
}) => {
  const isFromMe = isMessageByMe(hiMessage, currentUserId);

  const key = `hiMessage-${hiMessage.id}`;

  const localMessage = convertHiMessageToLocal(hiMessage, {
    session_id: sessionId,
    other_side_id: otherSideId as Web3ID,
    my_id: currentUserId as Web3ID,
  });

  const observerUnread = useMemo(
    () => isUnreadMessageByOtherSide(hiMessage, currentUserId as Web3ID),
    [hiMessage, currentUserId],
  );

  const handleChange = useCallback(
    (entry: any) => {
      if (observerUnread && entry.isIntersecting) {
        readMessage(hiMessage.id);
      }
    },
    [hiMessage, observerUnread, readMessage],
  );

  if (isSystemMessage(hiMessage)) {
    return (
      <div key={key}>
        <Observer onChange={handleChange}>
          <div>
            <SystemMessageItem message={localMessage} otherSideAccountName={otherSideAccountName} />
          </div>
        </Observer>
      </div>
    );
  }

  if (isUnchargedOfferMessage(hiMessage) && !isFromMe) {
    return (
      <div key={key}>
        <Observer onChange={handleChange}>
          <div>
            <UnchargedMessageItem
              hiMessage={localMessage}
              onCharge={() => {
                chargeOffer(hiMessage.offer!.id, sessionId, hiMessage.id);
              }}
            />
          </div>
        </Observer>
      </div>
    );
  }

  return (
    <div key={key}>
      <Observer onChange={handleChange}>
        <div>
          <NormalMessageItem
            messageInfo={localMessage}
            otherSideAccountName={otherSideAccountName}
            otherSideUser={otherSideUser}
          />
        </div>
      </Observer>
    </div>
  );
};

export default HiMessageItem;
